import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FilterTagCombobox from './primitives/FilterTagCombobox';

interface Country {
  code: string;
  name: string;
  flag: string;
}

const COUNTRIES: Country[] = [
  { code: 'FR', name: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'US', name: 'United States', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'GB', name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'DE', name: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'ES', name: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'IT', name: 'Italy', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'BR', name: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'CA', name: 'Canada', flag: '\u{1F1E8}\u{1F1E6}' },
  { code: 'AU', name: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: 'JP', name: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'NL', name: 'Netherlands', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'BE', name: 'Belgium', flag: '\u{1F1E7}\u{1F1EA}' },
  { code: 'CH', name: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: 'PT', name: 'Portugal', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'SE', name: 'Sweden', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'DK', name: 'Denmark', flag: '\u{1F1E9}\u{1F1F0}' },
  { code: 'NO', name: 'Norway', flag: '\u{1F1F3}\u{1F1F4}' },
  { code: 'MX', name: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: 'AR', name: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
  { code: 'IN', name: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'KR', name: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'MA', name: 'Morocco', flag: '\u{1F1F2}\u{1F1E6}' },
  { code: 'TN', name: 'Tunisia', flag: '\u{1F1F9}\u{1F1F3}' },
  { code: 'DZ', name: 'Algeria', flag: '\u{1F1E9}\u{1F1FF}' },
  { code: 'SA', name: 'Saudi Arabia', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: 'AE', name: 'UAE', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: 'PL', name: 'Poland', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'IE', name: 'Ireland', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: 'AT', name: 'Austria', flag: '\u{1F1E6}\u{1F1F9}' },
  { code: 'SN', name: 'Senegal', flag: '\u{1F1F8}\u{1F1F3}' },
  { code: 'CI', name: "Cote d'Ivoire", flag: '\u{1F1E8}\u{1F1EE}' },
];

const COUNTRY_MAP = new Map(COUNTRIES.map((c) => [c.code, c]));

/** Find country metadata by code */
export const getCountryByCode = (code: string): Country | undefined => COUNTRY_MAP.get(code);

/** Combobox items: "flag Name" display, code as value */
const COMBOBOX_ITEMS = COUNTRIES.map((c) => ({
  label: `${c.flag} ${c.name}`,
  value: c.code,
}));

interface CountryMultiSelectProps {
  value: string[] | null | undefined;
  onChange: (countries: string[] | null) => void;
  maxSelected?: number;
  /** Restrict the selectable countries to these codes (defaults to all). */
  allowedCodes?: readonly string[];
  disabled?: boolean;
}

/** Thin country-flavoured wrapper around the generic FilterTagCombobox. */
const CountryMultiSelect = ({
  value,
  onChange,
  maxSelected,
  allowedCodes,
  disabled,
}: CountryMultiSelectProps) => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    if (!allowedCodes) return COMBOBOX_ITEMS;
    const allowed = new Set(allowedCodes);
    return COMBOBOX_ITEMS.filter((item) => allowed.has(item.value));
  }, [allowedCodes]);

  const formatTag = useMemo(
    () => (code: string) => {
      const country = COUNTRY_MAP.get(code);
      return country ? `${country.flag} ${code}` : code;
    },
    []
  );

  return (
    <FilterTagCombobox
      items={items}
      value={value}
      onChange={onChange}
      maxSelected={maxSelected}
      disabled={disabled}
      placeholder={t('creatorDiscovery.filters.noCountriesSelected')}
      searchPlaceholder={t('creatorDiscovery.filters.searchCountry')}
      formatTag={formatTag}
    />
  );
};

export default CountryMultiSelect;
